import type {ReactElement} from 'react';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useAiChatSeason} from '@AiChat/hooks/useAiChatSeason';
import {AiChatWindowInspirations} from '@AiChat/components/ai-chat-window-inspirations';
import type {InspirationData} from '@AiChat/components/ai-chat-window-inspirations';
import {AiChatHalloweenPumpkins} from '@AiChat/components/ai-chat-halloween-pumpkins/ai-chat-halloween-pumpkins';
import {AiChatChristmasBackdrop} from '@AiChat/components/ai-chat-christmas-backdrop/ai-chat-christmas-backdrop';
import {AiChatChristmasBackdropPlacement} from '@AiChat/components/ai-chat-christmas-backdrop/ai-chat-christmas-backdrop.types';
import {AiChatSeasonSuggestionPills} from '@AiChat/components/ai-chat-season-suggestion-pills/ai-chat-season-suggestion-pills';
import {getAiChatSeasonInspirations} from '@AiChat/lib/ai-chat-season-library';
import {AI_CHAT_SEASON_MOBILE_INSPIRATION_COUNT, AiChatSeason} from '@AiChat/lib/ai-chat-season-library.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {assertNever} from '@Utils/assert-never.util';
import type {AiChatSeasonalEmptyStateProps} from './ai-chat-seasonal-empty-state.types';
import styles from './ai-chat-seasonal-empty-state.module.scss';

export function AiChatSeasonalEmptyState({inspirations}: AiChatSeasonalEmptyStateProps): ReactElement {
  const {state} = useAiChat();
  const {isSmall} = useAiChatSize();
  const season = useAiChatSeason();
  const isInspirationsGridCompact = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);
  const {actions: promptBoxActions} = useAiPromptBox(state.promptBoxId);

  if (!season) {
    return <AiChatWindowInspirations inspirations={inspirations} />;
  }

  const getSuggestionPills = (): ReactElement | null => {
    if (isSmall) {
      return null;
    }

    return <AiChatSeasonSuggestionPills season={season} />;
  };

  const getSeasonInspirations = (): InspirationData[] => {
    const seasonInspirations = getAiChatSeasonInspirations(season, promptBoxActions.update);
    if (isSmall) {
      return seasonInspirations.slice(0, AI_CHAT_SEASON_MOBILE_INSPIRATION_COUNT);
    }

    return seasonInspirations;
  };

  const getChristmasCardPlacement = (): AiChatChristmasBackdropPlacement => {
    if (isInspirationsGridCompact) {
      return AiChatChristmasBackdropPlacement.COMPACT_CARD;
    }

    return AiChatChristmasBackdropPlacement.CARD;
  };

  const getCardsDecoration = (): ReactElement => {
    switch (season) {
      case AiChatSeason.HALLOWEEN:
        return <AiChatHalloweenPumpkins pumpkinSetClasses={`${styles.cardPumpkins} ${isInspirationsGridCompact ? styles.cardPumpkinsCompact : ''}`} />;
      case AiChatSeason.CHRISTMAS:
        return <AiChatChristmasBackdrop placement={getChristmasCardPlacement()} />;
      default:
        return assertNever(season);
    }
  };

  return (
    <>
      {getSuggestionPills()}
      <div className={`${styles.decoratedInspirations} ${isInspirationsGridCompact ? styles.decoratedInspirationsCompact : ''}`}>
        <AiChatWindowInspirations inspirations={[...getSeasonInspirations(), ...inspirations]} cardsDecoration={getCardsDecoration()} />
      </div>
    </>
  );
}
